import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, SpinLoading, Toast } from 'antd-mobile';
import { CheckCircleOutline, CloseCircleOutline } from 'antd-mobile-icons';
import { signIn } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './SignPage.css';

export default function SignPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, initialized, isAuthenticated } = useAuth();
  const [signing, setSigning] = useState(false);
  const [signResult, setSignResult] = useState(null); // { success: true/false, message: string, signCount?: number }
  const hasProcessedRef = useRef(false); // 防止重复处理

  useEffect(() => {
    // 防止重复处理
    if (hasProcessedRef.current) {
      return;
    }

    // 等待认证状态初始化
    if (!initialized) {
      return;
    }

    // 检查二维码参数
    const code = searchParams.get('code');
    if (!code) {
      hasProcessedRef.current = true;
      Toast.show({
        icon: 'fail',
        content: 'Invalid check-in link',
      });
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
      return;
    }

    // 如果未登录，保存签到码并跳转到登录页
    if (!isAuthenticated || !user?.id) {
      hasProcessedRef.current = true;
      // 保存签到码到 localStorage，登录后可以继续签到
      localStorage.setItem('pending-sign-code', code);
      Toast.show({
        icon: 'fail',
        content: 'Please sign in first',
      });
      setTimeout(() => {
        // 跳转到登录页，登录成功后会自动回到签到流程
        navigate(`/login?redirect=/sign?code=${encodeURIComponent(code)}`, { replace: true });
      }, 1500);
      return;
    }

    // 如果已经签到过，不再重复签到
    if (signing || signResult) {
      return;
    }

    // 标记为已处理，防止重复执行
    hasProcessedRef.current = true;

    // 自动执行签到
    const handleSign = async () => {
      setSigning(true);
      try {
        const result = await signIn(code, user.id);
        setSignResult({
          success: true,
          message: 'Check-in successful',
          signCount: result.signCount,
        });
        Toast.show({
          icon: 'success',
          content: 'Check-in successful',
        });

        // 清除待处理的签到码
        localStorage.removeItem('pending-sign-code');

        // 3秒后跳转到首页
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } catch (error) {
        setSignResult({
          success: false,
          message: error.message || 'Check-in failed. Please try again.',
        });
        Toast.show({
          icon: 'fail',
          content: error.message || 'Check-in failed. Please try again.',
        });

        // 3秒后跳转到首页
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } finally {
        setSigning(false);
      }
    };

    handleSign();
  }, [searchParams, initialized, isAuthenticated, user, navigate]);

  // 显示加载状态
  if (!initialized || signing) {
    return (
      <div className="sign-page">
        <div className="sign-container">
          <SpinLoading color="primary" style={{ fontSize: '48px' }} />
          <div className="sign-loading-text">Checking in...</div>
        </div>
      </div>
    );
  }

  // 显示签到结果
  if (signResult) {
    return (
      <div className="sign-page">
        <div className="sign-container">
          <Card className="sign-result-card">
            {signResult.success ? (
              <>
                <CheckCircleOutline
                  style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }}
                />
                <div className="sign-result-title">Check-in successful</div>
                <div className="sign-result-message">{signResult.message}</div>
                {signResult.signCount !== undefined && (
                  <div className="sign-count-info">
                    Total check-ins: {signResult.signCount}
                  </div>
                )}
              </>
            ) : (
              <>
                <CloseCircleOutline
                  style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '16px' }}
                />
                <div className="sign-result-title">Check-in failed</div>
                <div className="sign-result-message">{signResult.message}</div>
              </>
            )}
            <Button
              block
              color="primary"
              onClick={() => navigate('/', { replace: true })}
              style={{ marginTop: '24px' }}
            >
              Back to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // 默认状态（不应该到达这里，但为了安全起见）
  return (
    <div className="sign-page">
      <div className="sign-container">
        <SpinLoading color="primary" style={{ fontSize: '48px' }} />
        <div className="sign-loading-text">Processing...</div>
      </div>
    </div>
  );
}
